/**
 * Utility functions for handling edge function errors and responses
 */

export interface EdgeFunctionError {
  message: string;
  details?: string;
  error?: string;
  context?: {
    body?: any;
  };
}

/**
 * Extracts a meaningful error message from edge function errors
 * and includes the function name in the error message
 */
export function getEdgeFunctionErrorMessage(
  error: EdgeFunctionError,
  functionName: string
): string {
  // Try to extract the actual error message
  if (error?.message &&
      error.message !== 'Edge Function returned a non-2xx status code' &&
      error.message !== 'Failed to send a request to the Edge Function' &&
      error.message !== '[object Object]') {
    if (error.message === '[object Object]' || error.message.startsWith('[object ')) {
      // Skip this and try other extraction methods
    } else {
      return error.message;
    }
  }
  
  if (error?.details) {
    return error.details;
  }
  
  if (error?.error) {
    return error.error;
  }
  
  // Try to extract nested context messages from Supabase error shapes first
  const ctx: any = (error as any)?.context;
  const ctxMessage = (ctx?.value?.message || ctx?.message || ctx?.text);
  let blockedWarning: string | null = null;

  if (ctxMessage && typeof ctxMessage === 'string') {
    const lower = ctxMessage.toLowerCase();
    if (lower.includes('failed to fetch') || lower.includes('err_blocked_by_client')) {
      blockedWarning = ctxMessage;
    } else {
      return ctxMessage;
    }
  }

  // Try to parse the response body for more details
  const tryParse = (raw: any) => {
    if (!raw) return null;
    try {
      const asObj = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (asObj?.error) {
        const errorValue = asObj.error;
        if (typeof errorValue === 'string') return errorValue;
        if (errorValue?.message) return errorValue.message;
        return JSON.stringify(errorValue);
      }
      if (asObj?.message) return asObj.message;
      if (asObj?.details) return asObj.details;
      return null;
    } catch {
      if (typeof raw === 'string' && raw.length < 500) {
        return raw;
      }
      return null;
    }
  };

  // 0) Check error.context.responseData first
  if ((error as any)?.context?.responseData) {
    const responseData = (error as any).context.responseData;
    if (responseData?.error) {
      const errorValue = typeof responseData.error === 'string' 
        ? responseData.error 
        : tryParse(responseData.error);
      if (errorValue) return errorValue;
    }
    if (responseData?.message) {
      return responseData.message;
    }
  }

  // 1) Legacy shape: error.context.body
  const msgFromBody = tryParse(error?.context?.body);
  if (msgFromBody) return msgFromBody;

  // 2) Response shape: error.context.response.body or .text
  const resp = (error as any)?.context?.response;
  const msgFromResp =
    tryParse(resp?.body) ||
    tryParse(resp?.text) ||
    (typeof resp?.body === 'string' ? resp.body : null);

  if (msgFromResp) return msgFromResp;

  // 3) Try to extract from error.data
  const errorData = (error as any)?.data;
  const msgFromData = tryParse(errorData);
  if (msgFromData) return msgFromData;

  // 4) Try error.context.value
  const ctxValue = (error as any)?.context?.value;
  const msgFromCtxValue = tryParse(ctxValue);
  if (msgFromCtxValue) return msgFromCtxValue;

  // 5) Recursively search for error/message fields
  const searchForError = (obj: any, depth = 0): string | null => {
    if (depth > 3 || !obj || typeof obj !== 'object') return null;
    if (obj.error && typeof obj.error === 'string' && obj.error !== '[object Object]') return obj.error;
    if (obj.message && typeof obj.message === 'string' && obj.message !== '[object Object]') return obj.message;
    if (obj.error && typeof obj.error === 'object') {
      if (obj.error.message && typeof obj.error.message === 'string') return obj.error.message;
      if (obj.error.details && typeof obj.error.details === 'string') return obj.error.details;
    }
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && typeof obj[key] === 'object') {
        const found = searchForError(obj[key], depth + 1);
        if (found && found !== '[object Object]') return found;
      }
    }
    return null;
  };
  const recursiveError = searchForError(error);
  if (recursiveError && 
      recursiveError !== 'Edge Function returned a non-2xx status code' &&
      recursiveError !== '[object Object]') {
    return recursiveError;
  }

  // 6) Include status when available
  if (resp?.status || resp?.statusText) {
    const statusText = resp?.statusText ? ` (${resp.statusText})` : '';
    return `Error ${resp?.status ?? ''}${statusText}`;
  }

  if (blockedWarning) {
    return `Network request failed (${blockedWarning}). Check availability, CORS/SSL configuration, or disable ad/tracker blockers if applicable.`;
  }
  
  // Default fallback
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Creates a standardized error handler for edge function calls
 */
export function createEdgeFunctionErrorHandler(functionName: string) {
  return (error: EdgeFunctionError): string => {
    console.error(`Error in ${functionName}:`, error);
    return getEdgeFunctionErrorMessage(error, functionName);
  };
}